import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Tag,
  Space,
  Spin,
  message,
  Typography,
  Row,
  Col,
  Statistic,
  DatePicker,
  Button
} from 'antd';
import {
  ThunderboltOutlined,
  PlusCircleOutlined,
  MinusCircleOutlined,
  CalendarOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import governanceService from '@services/operator/governanceService';
import { useOperatorNotification } from '@contexts/operator/OperatorNotificationContext';
import ListPageHeader from '@shared/ListPageHeader';
import { formatDateTime } from '@utils/dateTime';

const { Text } = Typography;
const { RangePicker } = DatePicker;

const EVENT_TYPE_COLORS = {
  fulfillment_shipped: 'green',
  // Legacy event types — retained for historical entries
  order_created: 'default',
  items_modified: 'default',
  order_canceled: 'default',
  order_resumed: 'default'
};

const PcsLedger = () => {
  const { t } = useTranslation();
  const { addJob } = useOperatorNotification();
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [entries, setEntries] = useState([]);
  const [pagination, setPagination] = useState({ current_page: 1, per_page: 20, total_count: 0, total_pages: 0 });
  const [summary, setSummary] = useState(null);
  const [period, setPeriod] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [activePreset, setActivePreset] = useState('current');
  const [currentParams, setCurrentParams] = useState({});

  const fetchData = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      const [entriesRes, summaryRes] = await Promise.all([
        governanceService.getPcsLedgerEntries(params),
        governanceService.getPcsLedgerSummary(params)
      ]);

      if (entriesRes.success) {
        setEntries(entriesRes.data.pcs_ledger_entries);
        if (entriesRes.data.pagination) {
          setPagination(entriesRes.data.pagination);
        }
        setPeriod(entriesRes.data.period);
        if (entriesRes.data.billing_period) {
          setBillingPeriod(entriesRes.data.billing_period);
        }
      }

      if (summaryRes.success) {
        setSummary(summaryRes.data.summary);
      }
    } catch (error) {
      console.error('Failed to fetch PCS ledger:', error);
      message.error(t('common.error.fetch_failed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    // Initial load uses billing period (server default)
    fetchData();
  }, [fetchData]);

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    setActivePreset(null);
    if (dates && dates[0] && dates[1]) {
      const params = {
        start_date: dates[0].format('YYYY-MM-DD'),
        end_date: dates[1].format('YYYY-MM-DD'),
        page: 1
      };
      setCurrentParams(params);
      fetchData(params);
    } else {
      // Reset to billing period
      setActivePreset('current');
      setCurrentParams({});
      fetchData({ page: 1 });
    }
  };

  const handlePreset = (preset) => {
    setActivePreset(preset);
    if (!billingPeriod) return;

    if (preset === 'current') {
      setDateRange(null);
      setCurrentParams({});
      fetchData({ page: 1 });
    } else if (preset === 'previous') {
      // Calculate previous billing period
      const currentStart = dayjs(billingPeriod.start_date);
      const prevEnd = currentStart.subtract(1, 'day');
      const prevStart = prevEnd.date(currentStart.date()).subtract(1, 'month').add(1, 'day');
      // Use the billing day from current period
      const billingDay = currentStart.date();
      const prevPeriodStart = prevEnd.subtract(1, 'month').date(billingDay);

      const dates = [prevPeriodStart, prevEnd];
      setDateRange(dates);
      const params = {
        start_date: prevPeriodStart.format('YYYY-MM-DD'),
        end_date: prevEnd.format('YYYY-MM-DD'),
        page: 1
      };
      setCurrentParams(params);
      fetchData(params);
    }
  };

  const handleTableChange = (paginationConfig) => {
    const params = {
      ...currentParams,
      page: paginationConfig.current,
      per_page: paginationConfig.pageSize
    };
    setCurrentParams(params);
    fetchData(params);
  };

  const handleExport = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const exportParams = {
        start_date: currentParams.start_date,
        end_date: currentParams.end_date
      };
      const result = await governanceService.exportPcsLedger(exportParams);
      if (result.success) {
        const { job_id } = result.data;
        if (addJob) {
          addJob(job_id, 'operator.governance.pcs_ledger.export.job_name');
        }
        message.success(t('operator.governance.pcs_ledger.export.started'));
      } else {
        message.error(result.message || t('common.error.operation_failed'));
      }
    } catch (error) {
      console.error('Export PCS ledger failed:', error);
      message.error(t('common.error.operation_failed'));
    } finally {
      setTimeout(() => setExporting(false), 1000);
    }
  }, [exporting, currentParams, addJob, t]);

  const columns = [
    {
      title: t('operator.governance.pcs_ledger.order_number'),
      dataIndex: 'order_number',
      key: 'order_number',
      render: (val) => val || '-'
    },
    {
      title: t('operator.governance.pcs_ledger.fulfillment_number'),
      key: 'fulfillment_number',
      render: (_, record) => record?.metadata?.fulfillment_number || '-'
    },
    {
      title: t('operator.governance.pcs_ledger.event_type'),
      dataIndex: 'event_type',
      key: 'event_type',
      filters: [
        { text: t('operator.governance.pcs_ledger.event_types.fulfillment_shipped'), value: 'fulfillment_shipped' },
        { text: t('operator.governance.pcs_ledger.event_types.order_created'), value: 'order_created' },
        { text: t('operator.governance.pcs_ledger.event_types.items_modified'), value: 'items_modified' },
        { text: t('operator.governance.pcs_ledger.event_types.order_canceled'), value: 'order_canceled' },
        { text: t('operator.governance.pcs_ledger.event_types.order_resumed'), value: 'order_resumed' }
      ],
      onFilter: (value, record) => record.event_type === value,
      render: (type) => (
        <Tag color={EVENT_TYPE_COLORS[type] || 'default'} style={{ margin: 0 }}>
          {t(`operator.governance.pcs_ledger.event_types.${type}`)}
        </Tag>
      )
    },
    {
      title: t('operator.governance.pcs_ledger.pcs_quantity'),
      dataIndex: 'pcs_quantity',
      key: 'pcs_quantity',
      align: 'right',
      sorter: (a, b) => a.pcs_quantity - b.pcs_quantity,
      render: (val) => (
        <Space size={4}>
          {val > 0
            ? <PlusCircleOutlined style={{ color: '#52c41a', fontSize: 12 }} />
            : <MinusCircleOutlined style={{ color: '#ff4d4f', fontSize: 12 }} />
          }
          <Text strong style={{ color: val > 0 ? '#52c41a' : '#ff4d4f' }}>
            {val > 0 ? '+' : ''}{val.toLocaleString()}
          </Text>
        </Space>
      )
    },
    {
      title: t('operator.governance.pcs_ledger.recorded_at'),
      dataIndex: 'recorded_at',
      key: 'recorded_at',
      render: (val) => formatDateTime(val)
    }
  ];

  if (loading && !entries.length) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  const headerActions = (
    <>
      {billingPeriod && (
        <>
          <Button
            type={activePreset === 'previous' ? 'primary' : 'default'}
            icon={<CalendarOutlined />}
            onClick={() => handlePreset('previous')}
          >
            {t('operator.governance.pcs_ledger.previous_period')}
          </Button>
          <Button
            type={activePreset === 'current' ? 'primary' : 'default'}
            icon={<CalendarOutlined />}
            onClick={() => handlePreset('current')}
          >
            {t('operator.governance.pcs_ledger.current_period')}
          </Button>
        </>
      )}
      <RangePicker
        value={dateRange}
        onChange={handleDateRangeChange}
        allowClear
      />
      <Button
        icon={<DownloadOutlined />}
        onClick={handleExport}
        loading={exporting}
      >
        {t('operator.governance.pcs_ledger.export.button')}
      </Button>
    </>
  );

  return (
    <div>
      <ListPageHeader
        items={[
          'governance',
          { label: t('operator.menu.governance.subscription'), path: '/governance/subscription' },
          { label: t('operator.governance.pcs_ledger.title') }
        ]}
        title={t('operator.governance.pcs_ledger.title')}
        actions={headerActions}
      />

      {/* Period indicator */}
      {period && (
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            <CalendarOutlined style={{ marginRight: 4 }} />
            {t('operator.governance.pcs_ledger.showing_period')}: {period.start_date} ~ {period.end_date}
            {billingPeriod && activePreset === 'current' && (
              <Tag color="blue" style={{ margin: '0 0 0 8px', fontSize: 11 }}>
                {t('operator.governance.pcs_ledger.current_billing_period')}
              </Tag>
            )}
          </Text>
        </div>
      )}

      {/* Summary Cards */}
      {summary && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title={t('operator.governance.pcs_ledger.total_added')}
                value={summary.total_added}
                valueStyle={{ color: '#52c41a' }}
                prefix={<PlusCircleOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title={t('operator.governance.pcs_ledger.total_reversed')}
                value={summary.total_reversed}
                valueStyle={{ color: '#ff4d4f' }}
                prefix={<MinusCircleOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title={t('operator.governance.pcs_ledger.net_pcs')}
                value={summary.net_pcs}
                valueStyle={{ color: '#1890ff' }}
                prefix={<ThunderboltOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title={t('operator.governance.pcs_ledger.entry_count')}
                value={summary.entry_count}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Ledger Table */}
      <Card size="small">
        <Table
          dataSource={entries}
          columns={columns}
          rowKey={(record) => `${record.id}-${record.recorded_at}`}
          pagination={{
            current: pagination.current_page,
            pageSize: pagination.per_page,
            total: pagination.total_count,
            showSizeChanger: true,
            pageSizeOptions: ['20', '50', '100'],
            showTotal: (total) => `${total} ${t('common.items')}`
          }}
          onChange={handleTableChange}
          size="small"
          loading={loading}
        />
      </Card>
    </div>
  );
};

export default PcsLedger;
